import { defineStore } from 'pinia'
import { firebaseApp, firebaseAuth, firestoreDb } from '@/plugins/firebase'
import {
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  signInWithEmailAndPassword,
} from 'firebase/auth'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import type { Auth, AuthError, User } from 'firebase/auth'
import type { FirebaseApp } from 'firebase/app'
import type { Firestore, DocumentData } from 'firebase/firestore'
import type { AppHealthStatus } from '@/types/all'

type userMeta = {
  company: string
  displayName: string
  termsAccepted: boolean
}

type FirebaseState = {
  firebaseApp: FirebaseApp
  firebaseStore: Firestore
  firebaseAuth: Auth
  user: User | null
  userMeta: userMeta | null
  companyId: string | null
  displayName: string | null
  lastActivity: number
  inactivityIntervalId: number | null
  authInitialized: boolean
  hasError: boolean
}

export type AuthHeaders = {
  Authorization?: string
  'User-Id'?: string
  'Company-Id'?: string
}

export const useFirebaseStore = defineStore('firebase', {
  state: (): FirebaseState => ({
    firebaseApp: firebaseApp,
    firebaseStore: firestoreDb,
    firebaseAuth: firebaseAuth,
    user: null,
    userMeta: null,
    companyId: null,
    displayName: null,
    lastActivity: Date.now(),
    inactivityIntervalId: null,
    authInitialized: false,
    hasError: false,
  }),
  actions: {
    async getAuthHeadersForAPI(): Promise<AuthHeaders> {
      if (!this.user) return {}

      try {
        const token = await this.user.getIdToken(false)
        return {
          Authorization: `Bearer ${token}`,
          'User-Id': this.user.uid,
          'Company-Id': this.companyId || '',
        }
      } catch (error) {
        console.error('Failed to get auth token:', error)
        // Force sign out on token errors as they indicate serious auth problems
        await this.signOut()
        throw error
      }
    },
    async updateUserAcceptTerms(acceptTerms: boolean): Promise<void> {
      try {
        if (!this.user) throw new Error('User not authenticated')

        const userDocRef = doc(this.firebaseStore, 'user_companies', this.user.uid)
        await updateDoc(userDocRef, { termsAccepted: acceptTerms })
        this.userMeta!.termsAccepted = acceptTerms
      } catch (error) {
        console.error('Error updating user terms acceptance:', error)
        throw error
      }
    },

    async fetchUser(userId: string): Promise<DocumentData> {
      try {
        // console.log("Fetching company ID for user:", userId);
        const userDocRef = doc(this.firebaseStore, 'user_companies', userId)
        const userDoc = await getDoc(userDocRef)

        if (userDoc.exists() && userDoc.data().company) {
          return userDoc.data()
        }

        throw new Error('No company ID found for user')
      } catch (error) {
        console.error('Error fetching company ID:', error)
        throw error
      }
    },

    async signIn(email: string, password: string) {
      try {
        await signInWithEmailAndPassword(this.firebaseAuth, email, password)
        return this.firebaseAuth.authStateReady
      } catch (error) {
        const authError = error as AuthError
        console.error('Sign-in error:', authError.code, authError.message)

        // More specific error handling for better UX
        switch (authError.code) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
            throw new Error('Invalid email or password')
          case 'auth/too-many-requests':
            throw new Error('Too many sign-in attempts. Please try again later.')
          case 'auth/user-disabled':
            throw new Error('This account has been disabled')
          default:
            throw error
        }
      }
    },

    async signOut() {
      this.stopActivityMonitoring()
      this.companyId = null
      try {
        await this.firebaseAuth.signOut()
      } catch (error) {
        console.error('Error during sign out:', error)
        // Reset state even if signOut fails to ensure clean client state
        this.user = null
      }
    },

    monitorUserActivity() {
      const sessionTimeout = 60 * 60 * 1000 // 1 hour
      const inactivityCheckInterval = 5 * 60 * 1000 // 5 minutes

      // Reset existing interval if any
      this.stopActivityMonitoring()

      // Update last activity timestamp
      this.lastActivity = Date.now()

      // Set up event listeners for user activity
      const updateActivity = () => {
        this.lastActivity = Date.now()
      }

      document.addEventListener('click', updateActivity)
      document.addEventListener('keypress', updateActivity)
      document.addEventListener('mousemove', updateActivity)
      document.addEventListener('touchstart', updateActivity)

      // Set up interval to check for inactivity
      this.inactivityIntervalId = window.setInterval(() => {
        const inactiveTime = Date.now() - this.lastActivity
        const remainingTime = sessionTimeout - inactiveTime

        // Log less frequently to reduce console spam
        if (remainingTime < sessionTimeout / 2) {
          console.log('Session timeout in:', Math.floor(remainingTime / 60000), 'minutes')
        }

        if (inactiveTime > sessionTimeout) {
          console.log('Session timeout due to inactivity')
          this.signOut()
        }
      }, inactivityCheckInterval)
    },

    stopActivityMonitoring() {
      if (this.inactivityIntervalId) {
        clearInterval(this.inactivityIntervalId)
        this.inactivityIntervalId = null
      }
    },

    init() {
      // Set persistence first
      try {
        setPersistence(this.firebaseAuth, browserLocalPersistence)
      } catch (error) {
        const authError = error as AuthError
        console.log('Error setting persistence:', authError.code, authError.message)
      }

      // Return a promise that resolves when auth state is first established
      return new Promise<void>((resolve, reject) => {
        onAuthStateChanged(
          this.firebaseAuth,
          (user) => {
            if (user) {
              ;(async () => {
                try {
                  const userData = await this.fetchUser(user.uid)
                  this.user = user
                  this.userMeta = {
                    company: userData.company,
                    displayName: userData.displayName,
                    termsAccepted: userData.termsAccepted,
                  }
                  this.companyId = userData.company
                  this.displayName = userData.displayName
                  this.hasError = false
                  this.monitorUserActivity()
                  console.log('User signed in', this.user.email, this.companyId)
                } catch (error) {
                  console.error('Error fetching user data:', error)
                  this.user = null
                  this.companyId = null
                  this.displayName = null
                  this.stopActivityMonitoring()
                  this.hasError = true

                  // Force sign out if company ID couldn't be fetched
                  await this.firebaseAuth.signOut().catch((e) => {
                    console.error('Error signing out:', e)
                  })

                  reject(new Error('Failed to initialize authentication'))
                } finally {
                  // Indicate auth is initialized, regardless of outcome
                  this.authInitialized = true
                  resolve()
                }
              })()
            } else {
              this.user = null
              console.log('No user signed in')
              this.stopActivityMonitoring()
              // Indicate auth is initialized even with no user
              this.hasError = false
              this.authInitialized = true
              resolve()
            }
          },
          (error) => {
            console.error('Auth state change error:', error)
            this.hasError = true
            this.authInitialized = true
            reject(new Error('Failed to initialize authentication: ' + error.message))
          },
        )
      })
    },
  },

  getters: {
    isAuthenticated(state): boolean {
      return state.user !== null
    },
    hasAuthError(state): boolean {
      return state.hasError
    },
    currentCompanyId(state): string | null {
      return state.companyId || null
    },
    userDisplayName(state): string | null {
      return state.displayName || null
    },
    termsAccepted(state): boolean {
      return state.userMeta?.termsAccepted || false
    },
    healthStatus(state): AppHealthStatus {
      const appClientOk = state.firebaseApp !== null
      const appAuthOk = state.firebaseAuth !== null

      const user = {
        id: state.user?.uid || null,
        ok: state.user !== null,
      }

      const company = {
        id: state.companyId || null,
        ok: state.companyId !== null,
      }

      const overallOk = appClientOk && appAuthOk && user.ok && company.ok

      return {
        ok: overallOk,
        appClient: appClientOk,
        appAuth: appAuthOk,
        user: user,
        company: company,
      }
    },
  },
})
